export default class StateUtils {
  static getDoorsStateForNumber (doorsState, doorsNumber, hasSoftClose) {
    let localState = JSON.parse(JSON.stringify(doorsState));
    localState.number = doorsNumber;

    // adjust doors item length if needed
    if (localState.items.length > doorsNumber) {
      localState.items.splice(doorsNumber);
    } else {
      while (localState.items.length < doorsNumber) {
        localState.items.push({ division_id: null, soft_close: hasSoftClose, fillings: [{ filling_id: null }] });
      }
    }

    return localState;
  }

  static getDefaultWardrobePriceObject () {
    return {
      discount_id: null,
      discount_value: 0,
      door_price: 0,
      door_price_before_discount: 0,
      doors: [], // e.g. {: 0, normal_elements: 0, mirror_elements: 0, soft_close_price: 0, total_door_price: 0, divided: 2}
      doors_price: 0,
      gable_price: 0,
      impact_price: 0,
      inventory: {}, // e.g 2: 200.2
      inventory_price: 0,
      total_price: 0,
      total_price_before_discount: 0
    }
  }

  static getInventoryIDs (inventoryArray) {
    return inventoryArray.map(item => ({ inventory_id: item.id }))
  }

  static getIsWardrobeCreatorModeDisabled (creatorMode, stepsCompletion, isFromAutoSave) {
    if (isFromAutoSave) {
      return true;
    }
    if (!stepsCompletion.creatorType || !stepsCompletion.wardrobeDimensions) {
      return false;
    }

    let isDoorCompleted = false;
    if (creatorMode.hasDoor) {
      isDoorCompleted = stepsCompletion.wardrobeDoorsNumber && stepsCompletion.wardrobeDoorsDivision && stepsCompletion.wardrobeDoorsMaterial;
    } else {
      isDoorCompleted = true;
    }

    let isInventoryCompleted = false;
    if (creatorMode.hasInventory) {
      isInventoryCompleted = stepsCompletion.wardrobeInventory;
    } else {
      isInventoryCompleted = true;
    }

    let isPositionCompleted = false;
    if (creatorMode.hasDoor && creatorMode.hasInventory) {
      isPositionCompleted = stepsCompletion.wardrobePosition;
    } else {
      isPositionCompleted = true;
    }

    return isDoorCompleted && isInventoryCompleted && isPositionCompleted;
  }

  static getStepsForCreatorMode (creatorMode) {
    let steps = ['creatorType', 'wardrobeDimensions'];
    if (creatorMode.hasDoor && creatorMode.hasInventory) {
      steps.push('wardrobePosition');
    }
    if (creatorMode.hasDoor) {
      steps.push('wardrobeDoorsNumber');
      steps.push('wardrobeDoorsDivision');
      steps.push('wardrobeDoorsMaterial');
    }
    if (creatorMode.hasInventory) {
      steps.push('wardrobeInventory');
    }
    return steps;
  }

  static get ROUTE_NAME_MAP() {
    return {
      'creatorType': 'startScreen',
      'wardrobeDimensions': 'wardrobeDimensions',
      'wardrobePosition': 'wardrobePosition',
      'wardrobeDoorsNumber': 'wardrobeDoorsNumber',
      'wardrobeDoorsDivision': 'wardrobeDoorsDivision',
      'wardrobeDoorsMaterial': 'wardrobeDoorsMaterial',
      'wardrobeInventory': 'wardrobeInventory'
    };
  }

  static isWardrobeNew (creatorWardrobeData, wardrobesList) {
    const wardrobeInCart = wardrobesList.find(item => item.id === creatorWardrobeData.id);
    return !wardrobeInCart || !wardrobeInCart.creatorMode;
  }

  static isWardrobeChanged (creatorWardrobeData, wardrobesList) {
    const wardrobeInCart = wardrobesList.find(item => item.id === creatorWardrobeData.id);

    if (!wardrobeInCart || !wardrobeInCart.creatorMode) {
      return false;
    }

    if (this.isCreatorTypeChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isDimensionsChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isPositionChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isDoorsNumberChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isDoorsDivisionChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isMaterialsChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    if (this.isInventoryChanged(creatorWardrobeData, wardrobeInCart)) {
      return true;
    }

    return false;
  }

  static isCreatorTypeChanged (currentState, prevState) {
    if (currentState.creatorMode.hasDoor !== prevState.creatorMode.hasDoor) {
      return true;
    }

    if (currentState.creatorMode.hasInventory !== prevState.creatorMode.hasInventory) {
      return true;
    }

    return false;
  }

  static isDimensionsChanged (currentState, prevState) {
    if (Number(currentState.wardrobe.width) !== Number(prevState.wardrobe.width)) {
      return true;
    }

    if (Number(currentState.wardrobe.height) !== Number(prevState.wardrobe.height)) {
      return true;
    }

    return false
  }

  static isPositionChanged (currentState, prevState) {
    if (Number(currentState.wardrobe.position_id) !== Number(prevState.wardrobe.position_id)) {
      return true;
    }
    if (Number(currentState.wardrobe.gable_id) !== Number(prevState.wardrobe.gable_id)) {
      return true;
    }
    if (currentState.wardrobe.impact !== prevState.wardrobe.impact) {
      return true;
    }

    return false
  }

  static isDoorsNumberChanged (currentState, prevState) {
    if (Number(currentState.doors.number) !== Number(prevState.doors.number)) {
      return true;
    }

    if (currentState.doors.items[0].soft_close !== prevState.doors.items[0].soft_close) {
      return true;
    }

    return false;
  }

  static isDoorsDivisionChanged (currentState, prevState) {
    if (currentState.doors.items.length !== prevState.doors.items.length) {
      return true;
    }

    return currentState.doors.items.some((doorItem, itemIndex) => {
      const prevStateItem = prevState.doors.items[itemIndex];
      if (!prevStateItem) {
        return true;
      }

      return doorItem.division_id !== prevStateItem.division_id;
    });
  }

  static isMaterialsChanged (currentState, prevState) {
    if (currentState.doors.items.length !== prevState.doors.items.length) {
      return true;
    }

    if (Number(currentState.wardrobe.gable_filling_id) !== Number(prevState.wardrobe.gable_filling_id)) {
      return true;
    }
    if (Number(currentState.wardrobe.impact_filling_id) !== Number(prevState.wardrobe.impact_filling_id)) {
      return true;
    }

    return currentState.doors.items.some((doorItem, itemIndex) => {
      const prevStateItem = prevState.doors.items[itemIndex];

      if (!prevStateItem || !prevStateItem.fillings) {
        return true;
      }

      return doorItem.fillings.some((filling, fillingIndex) => {
        const prevFilling = prevStateItem.fillings[fillingIndex];

        if (!prevFilling) {
          return true;
        }

        return filling.filling_id !== prevFilling.filling_id;
      });
    });
  }

  static isInventoryChanged (currentState, prevState) {
    if (currentState.inventory.length !== prevState.inventory.length) {
      return true;
    }

    const mapInventory = (inv) => inv.map(item => ({
      id: item.id,
      posX: Math.round(item.posX),
      posY: Math.round(item.posY),
      attachedCount: item.attachedItems ? item.attachedItems.length : 0
    })).sort((a, b) => a.id - b.id || a.posX - b.posX);

    const currentMap = mapInventory(currentState.inventory);
    const prevMap = mapInventory(prevState.inventory);

    return JSON.stringify(currentMap) !== JSON.stringify(prevMap);
  }
}
